import { registerConfettiTrigger, unregisterConfettiTrigger } from '@mxa-argos/lib/confetti';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const CONFETTI_IMAGES = Array.from({ length: 14 }, (_, i) => `/confetti/${i + 1}.webp`);

const PARTICLE_COUNT = 90;
const MAX_DELAY_S = 1.5;
const CLEANUP_DELAY_MS = 5000;

interface Particle {
  id: number;
  image: string;
  left: number;
  delay: number;
  duration: number;
  size: number;
  rotation: number;
  drift: number;
}

function generateParticles(): Particle[] {
  return Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
    id: i,
    image: CONFETTI_IMAGES[Math.floor(Math.random() * CONFETTI_IMAGES.length)],
    left: Math.random() * 100,
    delay: Math.random() * MAX_DELAY_S,
    duration: 1.5 + Math.random() * 1.0,
    size: 72 + Math.random() * 56,
    rotation: Math.random() * 720 - 360,
    drift: -50 + Math.random() * 100,
  }));
}

export function ConfettiProvider({ children }: { children: React.ReactNode }) {
  const [particles, setParticles] = useState<Particle[] | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);
  const batchRef = useRef(0);

  const triggerConfetti = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setParticles(null);
    batchRef.current += 1;
    requestAnimationFrame(() => {
      setParticles(generateParticles());
      timeoutRef.current = setTimeout(
        () => setParticles(null),
        MAX_DELAY_S * 1000 + CLEANUP_DELAY_MS,
      );
    });
  }, []);

  useEffect(() => {
    registerConfettiTrigger(triggerConfetti);
    return () => {
      unregisterConfettiTrigger();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [triggerConfetti]);

  return (
    <>
      {children}
      {particles && <ConfettiOverlay key={batchRef.current} particles={particles} />}
    </>
  );
}

function ConfettiOverlay({ particles }: { particles: Particle[] }) {
  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[9999] overflow-hidden">
      {particles.map((particle) => (
        <img
          key={particle.id}
          src={particle.image}
          alt=""
          className="absolute -top-32 animate-confetti-fall"
          style={
            {
              left: `${particle.left}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
              '--confetti-rotation': `${particle.rotation}deg`,
              '--confetti-drift': `${particle.drift}px`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>,
    document.body,
  );
}
